import React, { useEffect, useMemo, useRef, useState } from "react";
import { MessageSquare, Send, X, Sparkles, Loader2 } from "lucide-react";
import { message as antMessage } from "antd";
import AIService from "../services/AIService";
import { OPENAI_LATEST_MODEL } from "../services/modelCatalog";
import {
  TASK_KIND,
  finishTask,
  startTask,
} from "../stores/statusStore";

const ROLE_LABEL = { user: "You", assistant: "AI" };

const summarizeNodes = (nodes = [], topN = 30) => {
  const sorted = [...nodes]
    .filter((n) => n && (n.type === "concept" || n.type === "variation"))
    .sort((a, b) => (b.spend || 0) - (a.spend || 0))
    .slice(0, topN);
  return sorted.map((n) => ({
    id: n.id,
    label: n.label,
    type: n.type,
    spend: n.spend || 0,
    roas: n.roas || 0,
    ctr: n.ctr || 0,
    hookRate: n.hookRate || 0,
    confidence: n.confidence || 0,
    status: n.status,
    funnel: n.funnel,
    angle: n.angle,
    awareness: n.awareness,
    format: n.format,
  }));
};

const buildSystemPrompt = (context) => {
  const { selectedNode, summary, range, totalNodes, topNodes } = context || {};
  const lines = [];
  lines.push(
    "You are an ad creative analyst working with a force-directed graph of the user's Meta ad account.",
    "Reason from the data the user provides. Cite KB sources inline as [KB: <source>, <locator>] when relevant.",
    "Be concise and specific. Reference creative names and metrics from the supplied JSON when answering.",
  );
  if (range) {
    lines.push(`Time range under review: ${range.since} → ${range.until}.`);
  }
  if (typeof totalNodes === "number") {
    lines.push(`Account total: ${totalNodes} concept/variation nodes.`);
  }
  if (selectedNode) {
    lines.push(
      `Selected node: ${JSON.stringify({
        id: selectedNode.id,
        label: selectedNode.label,
        type: selectedNode.type,
        spend: selectedNode.spend,
        roas: selectedNode.roas,
        ctr: selectedNode.ctr,
        hookRate: selectedNode.hookRate,
        confidence: selectedNode.confidence,
        status: selectedNode.status,
        funnel: selectedNode.funnel,
        angle: selectedNode.angle,
      })}`,
    );
  }
  if (summary) {
    lines.push(`Summary metrics: ${JSON.stringify(summary)}`);
  }
  if (topNodes?.length) {
    lines.push(
      `Top spenders (truncated): ${JSON.stringify(topNodes.slice(0, 30))}`,
    );
  }
  return lines.join("\n");
};

export default function WebChatPanel({
  open,
  onClose,
  workspaceId,
  selectedNode,
  nodes,
  edges,
  summary,
  range,
  analysisModel,
  apiKeys,
  aiProfile,
  analysisKnowledge,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, open]);

  const topNodes = useMemo(() => summarizeNodes(nodes || []), [nodes]);
  const totalNodes = useMemo(
    () =>
      (nodes || []).filter(
        (n) => n && (n.type === "concept" || n.type === "variation"),
      ).length,
    [nodes],
  );

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || busy) return;

    if (!workspaceId) {
      antMessage.warning("Pick an active workspace before chatting.");
      return;
    }

    const next = [
      ...messages,
      { role: "user", content: text, ts: Date.now() },
    ];
    setMessages(next);
    setInput("");
    setBusy(true);

    const taskId = `chat:${Date.now()}`;
    startTask(taskId, { kind: TASK_KIND.CHAT, label: text.slice(0, 60) });

    try {
      const aiService = new AIService({
        ...(apiKeys || {}),
        aiProfile,
        analysisKnowledge,
        workspaceId,
      });
      await aiService.loadActiveKnowledge?.();

      const systemPrompt =
        buildSystemPrompt({
          selectedNode,
          summary,
          range,
          totalNodes,
          topNodes,
        }) +
        (aiService.buildKnowledgeContext
          ? aiService.buildKnowledgeContext({ topic: text, maxTokens: 4000 })
          : "");

      const useClaude =
        String(analysisModel || "").toLowerCase().startsWith("claude");

      let assistantText = "";

      if (useClaude) {
        const data = await aiService.callAiServer("anthropic.messages", {
          model: analysisModel,
          max_tokens: 1200,
          system: systemPrompt,
          messages: next.map((m) => ({
            role: m.role,
            content: [{ type: "text", text: m.content }],
          })),
        });
        const blocks = Array.isArray(data?.content) ? data.content : [];
        assistantText = blocks
          .map((b) => (typeof b?.text === "string" ? b.text : ""))
          .join("\n")
          .trim();
      } else {
        const data = await aiService.callAiServer("openai.chat", {
          model: analysisModel || OPENAI_LATEST_MODEL,
          messages: [
            { role: "system", content: systemPrompt },
            ...next.map((m) => ({ role: m.role, content: m.content })),
          ],
          temperature: 0.4,
        });
        assistantText =
          data?.choices?.[0]?.message?.content ||
          data?.output_text ||
          "";
      }

      if (!assistantText) {
        assistantText = "(No response.)";
      }

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: assistantText, ts: Date.now() },
      ]);
      finishTask(taskId, "done");
    } catch (error) {
      const msg = String(error?.message || error || "Chat failed");
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `_Error_: ${msg}`,
          ts: Date.now(),
          error: true,
        },
      ]);
      finishTask(taskId, "error", { error: msg });
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  if (!open) return null;

  return (
    <aside className="web-chat" role="complementary" aria-label="AI chat">
      <header className="web-chat__head">
        <div className="web-chat__head-title">
          <MessageSquare size={14} />
          Ask the AI about this view
        </div>
        <div className="web-chat__head-meta">
          <span title="Model from Settings · analysisModel">
            <Sparkles size={11} /> {analysisModel || "default"}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close chat"
            className="web-chat__close"
          >
            <X size={14} />
          </button>
        </div>
      </header>

      <div className="web-chat__thread" ref={scrollerRef}>
        {messages.length === 0 ? (
          <div className="web-chat__empty">
            Ask things like “Which TOF concepts have the highest ROAS?” or
            “What pattern do my fatigued ads share?”. The AI sees the top spenders
            in this view plus your knowledge base.
          </div>
        ) : (
          messages.map((msg, i) => (
            <div
              key={`${msg.ts}-${i}`}
              className={`web-chat__msg web-chat__msg--${msg.role}${msg.error ? " web-chat__msg--error" : ""}`}
            >
              <div className="web-chat__msg-role">{ROLE_LABEL[msg.role] || msg.role}</div>
              <div className="web-chat__msg-body">{msg.content}</div>
            </div>
          ))
        )}
      </div>

      <div className="web-chat__composer">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about this data…"
          rows={2}
          disabled={busy}
          className="web-chat__input"
        />
        <button
          type="button"
          onClick={sendMessage}
          disabled={busy || !input.trim()}
          className="web-chat__send"
        >
          {busy ? <Loader2 size={14} className="web-chat__spin" /> : <Send size={14} />}
          {busy ? "Thinking…" : "Send"}
        </button>
      </div>
    </aside>
  );
}
